import { useEffect, useState } from 'react';
import type { CelebrationCue, IsoDate } from '@contracts';
import { ChevronLeft, ChevronRight, Check, Sunrise, Sunset } from '@ui/shared/icons';
import { computeSunTimes } from '@ui/shared/format/solar';
import { getStoredLocation } from '@ui/shared/settings/location';
import { addDaysToDate } from '@domain/habits/week';
import { DatePickerPopover } from '@ui/shared/primitives/DatePickerPopover';

const WEEKDAY = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const MONTH = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** ISO week number (Mon-Sun weeks, ADR-0006) — plain client-side computation, ambient chrome only. */
function isoWeekNumber(isoDate: string): number {
  const date = new Date(`${isoDate}T00:00:00`);
  const target = new Date(date.valueOf());
  const dayNumber = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNumber = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNumber + 3);
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

function kickerFor(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  const date = new Date(`${isoDate}T00:00:00`);
  return `${WEEKDAY[date.getDay()]}, ${MONTH[(m ?? 1) - 1]} ${d} · WEEK ${isoWeekNumber(isoDate)}`;
}

/** Compact "Mon 7" style label for the day-paging cluster's date-jump
 *  trigger — distinct from `kickerFor`'s fuller weekday/week-number line,
 *  which stays in the header regardless of which day is being viewed. */
function shortDate(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  const date = new Date(`${isoDate}T00:00:00`);
  return `${WEEKDAY[date.getDay()]!.slice(0, 3)} ${d}, ${MONTH[(m ?? 1) - 1]}`;
}

/**
 * Header (design/screens/today-desktop-v2.md §4). Top row: kicker + week +
 * day-paging + sunrise/sunset + Close-the-day. Headline row: single editable
 * text line, click-to-edit inline. Sunrise/sunset is a PURE presentation
 * computation (`computeSunTimes`) — zero event-log involvement.
 */
export function TodayHeaderDesktopV2({
  date,
  realToday,
  headline,
  dayClosed,
  onSetHeadline,
  onPageDate,
  onCloseDay,
  onOpenHistory,
}: {
  readonly date: IsoDate;
  /** Bug fix: what "today" actually is, injected by the parent (which
   *  itself accepts a test override) rather than this component silently
   *  reading `new Date()` — see TodayView.desktop.tsx's own comment on
   *  this prop for the full "real clock vs fixture clock" story. */
  readonly realToday: IsoDate;
  readonly headline: string;
  readonly dayClosed: boolean;
  readonly onSetHeadline: (next: string) => void;
  readonly onPageDate: (next: IsoDate) => void;
  readonly onCloseDay: () => Promise<CelebrationCue>;
  /**
   * Pre-existing "View history" reachability, preserved (not part of the
   * screen spec's binding SS4.1 composition, but the phase spec never asked
   * to REMOVE this feature or its only desktop-Today entry point — the
   * pre-Phase-6 header's "View history" link was the sole way to reach the
   * already-shipped Completion History screen from desktop Today; dropping
   * it silently would have made that whole screen unreachable). Rendered as
   * a quiet text-link beside the day-paging cluster, the closest analog the
   * new header has to the old link's own low-emphasis register.
   */
  readonly onOpenHistory: () => void;
}) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [headlineDraft, setHeadlineDraft] = useState(headline);
  const [editingHeadline, setEditingHeadline] = useState(false);

  useEffect(() => setHeadlineDraft(headline), [headline]);

  const isRealToday = date === realToday;
  // §4.1: "If no lat/long is configured yet, this pair does not render" — a
  // quiet, silent omission, never a fabricated San Francisco default. Only
  // computed at all once a Settings-configured location exists.
  const storedLocation = getStoredLocation();
  const sun = storedLocation
    ? computeSunTimes(new Date(`${date}T12:00:00`), {
        latitude: storedLocation.lat,
        longitude: storedLocation.long,
        timezone: storedLocation.timezone,
      })
    : { sunrise: null, sunset: null };

  async function handleClose() {
    setClosing(true);
    try {
      await onCloseDay();
    } finally {
      setClosing(false);
    }
  }

  function commitHeadline() {
    setEditingHeadline(false);
    if (headlineDraft !== headline) onSetHeadline(headlineDraft);
  }

  return (
    <header style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div
            style={{
              fontSize: 'var(--type-label-size)',
              lineHeight: 'var(--type-label-line)',
              letterSpacing: 'var(--type-label-tracking)',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              fontWeight: 600,
            }}
          >
            {kickerFor(date)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            {/* Bug fix (owner-reported + design-audit should-fix): this
                cluster previously collapsed to two chevrons, with the LEFT
                one both mislabeled "Yesterday" (it actually opened a
                multi-date popover, not a one-day page-back) AND the
                popover it opened rendered cropped off the top of the
                viewport. Restored to the spec's real three-part control: a
                chevron that pages back exactly one day (matching Tomorrow's
                own one-day-forward behavior), a separate visible date-jump
                label, and the forward chevron unchanged.

                Bug fix (owner-reported, follow-up): the date-jump label
                originally opened `BackfillDatePopover` -- a short SCROLLING
                LIST bounded to the prior two Mon-Sun weeks (built for the
                phone footer's occasional-backfill use case, per that
                component's own doc comment). On desktop, jumping to any
                past day is a routine reflection action, not an occasional
                catch-up -- a flat list gets worse, not just slower, as more
                days accumulate real history. Swapped to `DatePickerPopover`
                (the same real month-grid calendar `ObjectiveFormScreen`/
                `ActionsTable` already use for target/due dates) -- genuinely
                unbounded, any month reachable via the chevrons, no list to
                scroll. Future dates are still clamped to `realToday` on
                select (mirroring the Tomorrow chevron's own disabled-at-
                today rule) since viewing a day that hasn't happened yet
                isn't a meaningful state for this screen. */}
            <button
              type="button"
              aria-label="Yesterday"
              onClick={() => onPageDate(addDaysToDate(date, -1) as IsoDate)}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)' }}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <div style={{ position: 'relative', minWidth: '320px' }}>
              <button
                type="button"
                aria-label="Jump to a date"
                aria-haspopup="dialog"
                aria-expanded={datePickerOpen}
                onClick={() => setDatePickerOpen((open) => !open)}
                className="num"
                style={{
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-2)',
                  borderRadius: 'var(--radius-md)',
                  color: isRealToday ? 'var(--hue-primary)' : 'var(--text-primary)',
                  fontWeight: 600,
                  fontSize: 'var(--type-caption-size)',
                }}
              >
                {isRealToday ? 'Today' : shortDate(date)}
              </button>
              {datePickerOpen && (
                <DatePickerPopover
                  selected={date}
                  today={realToday}
                  onSelect={(picked) => {
                    setDatePickerOpen(false);
                    onPageDate((picked > realToday ? realToday : picked) as IsoDate);
                  }}
                  onClose={() => setDatePickerOpen(false)}
                />
              )}
            </div>
            <button
              type="button"
              aria-label="Tomorrow"
              disabled={isRealToday}
              onClick={() => onPageDate(addDaysToDate(date, 1) as IsoDate)}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-md)',
                color: isRealToday ? 'var(--text-disabled)' : 'var(--text-muted)',
              }}
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flex: 'none' }}>
          {sun.sunrise && sun.sunset && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                <Sunrise size={16} color="var(--text-muted)" aria-hidden="true" />
                <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
                  {sun.sunrise}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                <Sunset size={16} color="var(--text-muted)" aria-hidden="true" />
                <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
                  {sun.sunset}
                </span>
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={onOpenHistory}
            style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}
          >
            View history
          </button>

          <CloseDayButton dayClosed={dayClosed} closing={closing} onClick={() => void handleClose()} />
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-3)' }}>
        {editingHeadline ? (
          <input
            autoFocus
            aria-label="Headline"
            value={headlineDraft}
            onChange={(e) => setHeadlineDraft(e.target.value)}
            onBlur={commitHeadline}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitHeadline();
            }}
            placeholder="What's today about?"
            style={{
              width: '100%',
              maxWidth: '480px',
              fontSize: 'var(--type-heading-size)',
              color: 'var(--text-primary)',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--border-strong)',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingHeadline(true)}
            style={{
              textAlign: 'left',
              fontSize: 'var(--type-heading-size)',
              color: headline ? 'var(--text-primary)' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              display: 'block',
            }}
          >
            {headline || "What's today about?"}
          </button>
        )}
      </div>
    </header>
  );
}

function CloseDayButton({
  dayClosed,
  closing,
  onClick,
}: {
  readonly dayClosed: boolean;
  readonly closing: boolean;
  readonly onClick: () => void;
}) {
  const label = closing ? 'Closing...' : dayClosed ? 'Day closed' : 'Close the day';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-busy={closing}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        minHeight: 'var(--touch-target-min)',
        padding: '0 var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: dayClosed ? 'var(--surface-raised)' : 'var(--hue-primary-vivid)',
        border: dayClosed ? '1px solid var(--border-strong)' : '1px solid var(--hue-primary-strong)',
        color: dayClosed ? 'var(--text-secondary)' : 'var(--text-on-accent)',
        fontWeight: 600,
        fontSize: 'var(--type-body-size)',
        whiteSpace: 'nowrap',
      }}
    >
      {dayClosed && <Check size={16} aria-hidden="true" />}
      {label}
    </button>
  );
}
